import type { Ref } from 'vue'
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getTownTimedelta from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getTownTimedelta'
import unwrapMedflexClasses from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/functions/unwrapMedflexClasses'
import getCategoryNavItems from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/functions/getCategoryNavItems'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types'
import type { MedflexServiceClass } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types/medflex'

type PricePageContext = ContextStore['context']

/**
 * Первый кадр прайса: только specialities_pricelist, без dprices и без списка услуг Medflex/синонимов.
 *
 * Не зависит от клиентского поиска — только SSR-контекст Django.
 */
export function isInitialSpecialistsOnlyPrice(context: PricePageContext): boolean {
  const specialities = unwrapList(context.specialities_pricelist)
  const categories = unwrapList(context.dprices)

  if (!specialities.length || categories.length) {
    return false
  }

  const serviceCategories = unwrapList(context.service_categories)
  const initialMedflexClasses = unwrapMedflexClasses(context.data)
  const hasInitialMedflexApi = Boolean(serviceCategories.length) || initialMedflexClasses.length > 0

  if (hasInitialMedflexApi) {
    return initialMedflexClasses.length === 0
  }

  return unwrapList(context.synonym_prices).length === 0
}

type Options = {
  serviceCategories: string[]
  serviceClasses: Ref<MedflexServiceClass[]>
  selectedCategoryName: Ref<string>
}

/**
 * Режим каталога цен: Medflex / synonym-fallback и ручной прайс (specialities + dprices).
 *
 * Списки независимы: Medflex и manual могут быть на странице одновременно,
 * как в legacy `services` + `full_prices`.
 *
 * Section передаёт выходы `useMedflexPrices`, composable их не вызывает.
 */
export default function usePriceCatalog({
  serviceCategories,
  serviceClasses,
  selectedCategoryName,
}: Options) {
  const { context }: ContextStore = useContextStore()
  const isMobile = useIsMobile()

  const specialities = computed(() => unwrapList(context.specialities_pricelist))
  const categories = computed(() => unwrapList(context.dprices))

  // Medflex-режим только при реальных категориях или классах услуг.
  // Один `num_pages` без data/categories даёт ложный поиск без списка.
  const hasMedflexApi = computed(() => (
    Boolean(serviceCategories.length)
    || unwrapMedflexClasses(context.data).length > 0
  ))

  const synonymFallback = computed(() => {
    const services = unwrapList(context.synonym_prices)

    if (hasMedflexApi.value || !services.length) {
      return []
    }

    return [{
      name: tStatic('Цены'),
      sublist: services.map(service => ({
        ...service,
        prices: { full: service.price },
      })),
    }]
  })

  const displayClasses = computed(() => (
    hasMedflexApi.value
      ? serviceClasses.value
      : synonymFallback.value
  ))

  const hasMedflexUi = computed(() => (
    hasMedflexApi.value || synonymFallback.value.length > 0
  ))

  const isSpecialistsOnly = computed(() => isInitialSpecialistsOnlyPrice(context))

  const categoryNavItems = computed(() => getCategoryNavItems(
    serviceCategories,
    displayClasses.value,
  ))

  // На mobile заголовки категорий в списке всегда видны.
  const hideGroupTitles = computed(() => (
    !isMobile.value && Boolean(selectedCategoryName.value)
  ))

  const emptySrc = `${context.global?.static?.src || '/static/'}_v1/pd/pictures/ui-kit/search.svg`

  const townTimedelta = computed(() => getTownTimedelta([
    context.town.timedelta,
    context.lpu.town.timedelta,
  ]))

  return {
    specialities,
    categories,
    hasMedflexApi,
    displayClasses,
    hasMedflexUi,
    isSpecialistsOnly,
    categoryNavItems,
    hideGroupTitles,
    emptySrc,
    townTimedelta,
  }
}
